import React, { useState } from 'react';
import { MapPin, Mail, Phone, Send, Facebook, Twitter, Instagram, Linkedin } from 'lucide-react';
import { siteConfig } from '../data/mock';
import contactHero from "../assets/images/contactusBg.webp";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    alert('Thank you for your message! We will get back to you soon.');
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="gradient-hero-saffron py-20"
        style={{
          backgroundImage: `
      linear-gradient(to bottom, rgba(255,247,230,0.85),rgba(192, 119, 51, 0.9)),
      url('${contactHero}')
    `,
          backgroundPosition: "center",
          backgroundSize: "contain",
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground font-serif">
              Contact Us
            </h1>
            <p className="text-xl text-muted-foreground">
              We'd love to hear from you - get in touch with us
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-5 gap-8">
              {/* Contact Information */}
              <div className="md:col-span-2 space-y-6">
                <div>
                  <h2 className="text-3xl font-bold text-foreground mb-6 font-serif">
                    Get In Touch
                  </h2>
                  <p className="text-muted-foreground leading-relaxed mb-8">
                    Whether you have questions about our programs, want to collaborate, or just want to say hello, we're here to help.
                  </p>
                </div>

                {/* Contact Details */}
                <div className="space-y-4">
                  <div className="flex items-start gap-4 p-4 bg-gradient-to-br from-secondary/20 to-white rounded-lg border border-primary/10">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">Address</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {siteConfig.address}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-gradient-to-br from-secondary/20 to-white rounded-lg border border-primary/10">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">Email</h3>
                      <a
                        href={`mailto:${siteConfig.email}`}
                        className="text-sm text-primary hover:underline"
                      >
                        {siteConfig.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-gradient-to-br from-secondary/20 to-white rounded-lg border border-primary/10">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">Phone</h3>
                      <div className="text-sm text-muted-foreground">
                        <a
                          href={`tel:${siteConfig.phone}`}
                          className="hover:text-primary transition-colors"
                        >
                          {siteConfig.phone}
                        </a>
                        <span> | </span>
                        <a
                          href={`tel:${siteConfig.phone2}`}
                          className="hover:text-primary transition-colors"
                        >
                          {siteConfig.phone2}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Social Media */}
                <div>
                  <h3 className="font-semibold text-foreground mb-4">Follow Us</h3>
                  <div className="flex gap-3">
                    <a
                      href={siteConfig.socialMedia.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center hover:bg-primary hover:text-white transition-colors"
                    >
                      <Facebook className="w-5 h-5" />
                    </a>
                    <a
                      href={siteConfig.socialMedia.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center hover:bg-primary hover:text-white transition-colors"
                    >
                      <Twitter className="w-5 h-5" />
                    </a>
                    <a
                      href={siteConfig.socialMedia.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center hover:bg-primary hover:text-white transition-colors"
                    >
                      <Instagram className="w-5 h-5" />
                    </a>
                    <a
                      href={siteConfig.socialMedia.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center hover:bg-primary hover:text-white transition-colors"
                    >
                      <Linkedin className="w-5 h-5" />
                    </a>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="md:col-span-3">
                <div className="bg-gradient-to-br from-secondary/20 to-white p-8 rounded-2xl shadow-xl border border-primary/10">
                  <h3 className="text-2xl font-bold text-foreground mb-6 font-serif">
                    Send Us a Message
                  </h3>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-foreground mb-2">
                          Your Name *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                          placeholder="Shane"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-foreground mb-2">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                          placeholder="shane@example.com"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-2">
                        Subject *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                        placeholder="What is this regarding?"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-2">
                        Message *
                      </label>
                      <textarea
                        required
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        rows={6}
                        className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                        placeholder="Tell us more about your inquiry..."
                      />
                    </div>

                    <button type="submit" className="w-full btn-saffron flex items-center justify-center gap-2">
                      <Send className="w-5 h-5" />
                      Send Message
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-secondary/20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-foreground mb-8 text-center font-serif">
              Find Us
            </h2>

            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d14012.969472602144!2d77.177363!3d28.592505!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390d1dc1c0455cb7%3A0x3b07918e5cc1dbe9!2sMaitreyi%20College%20University%20Of%20Delhi!5e0!3m2!1sen!2sin!4v1767615670203!5m2!1sen!2sin"
                className="w-full h-96 border-0"
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Maitreyi College, University of Delhi"
              />
            </div>

            {/* Optional Address Below Map */}
            <p className="text-center text-sm text-muted-foreground mt-4">
              {siteConfig.address}
            </p>
          </div>
        </div>
      </section>


      {/* Quick Contact CTA */}
      <section className="py-16 bg-gradient-to-br from-primary to-orange-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h2 className="text-3xl font-bold font-serif">
              Have Questions?
            </h2>
            <p className="text-xl text-white/90">
              Our team is ready to assist you with any inquiries
            </p>
            <a
              href={`mailto:${siteConfig.email}`}
              className="inline-flex items-center gap-2 bg-white text-primary font-bold px-8 py-4 rounded-full hover:bg-gray-100 transition-all hover:scale-105 shadow-xl"
            >
              <Mail className="w-5 h-5" />
              Email Us Directly
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
